import _ from 'lodash';

import { UserQuestionStatus } from 'constants/aiChat';

const EMPTY_LIST = [];

const getListForMessage = (state, messageId) => state?.[messageId] ?? EMPTY_LIST;

const findIndexByQuestionId = (list, questionId) =>
  list.findIndex(question => question.questionId === questionId);

const replaceAt = (list, index, next) => {
  const copy = [...list];
  copy[index] = next;

  return copy;
};

// DEV-4392: both the live `user_question_requested` SignalR event and the REST
// catch-up endpoint identify a parked ask_user_question batch by `toolCallId` /
// `toolName` (matching the answer path, where RespondToUserQuestion routes by
// toolCallId, and the confirmation sibling). The chat store keys its question
// entries by `questionId`, so normalize here at the single ingestion seam:
// `questionId === toolCallId`, `tool === toolName`. Accept either field name so
// a future shape change can't silently bail the whole card again.
const resolveQuestionId = source => source?.questionId ?? source?.toolCallId ?? null;
const resolveTool = source => source?.tool ?? source?.toolName ?? null;

// DEV-4392: a parked question is owned by exactly one message, but the two
// ingestion paths key it differently — the live event keys it to the streaming
// assistant message, while the REST catch-up keys it to the persisted owner (the
// user message). After a catch-up merge, drop any stale entry for the same
// questionId under a *different* message so the card can never render twice.
// Returns the same reference when there is nothing to prune (common case), so
// callers can cheaply detect a no-op.
const pruneQuestionIdsFromOtherMessages = (state, ownerMessageId, ownedIds) => {
  if (ownedIds.size === 0 || _.isNil(state)) {
    return state;
  }

  const hasStrayEntry = Object.entries(state).some(
    ([messageId, list]) =>
      messageId !== ownerMessageId && list.some(question => ownedIds.has(question.questionId)),
  );

  if (!hasStrayEntry) {
    return state;
  }

  return Object.entries(state).reduce((next, [messageId, list]) => {
    if (messageId === ownerMessageId) {
      next[messageId] = list;

      return next;
    }

    const filtered = list.filter(question => !ownedIds.has(question.questionId));

    if (filtered.length > 0) {
      next[messageId] = filtered;
    }

    return next;
  }, {});
};

export const appendPendingQuestion = (state, payload) => {
  const questionId = resolveQuestionId(payload);

  if (_.isNil(payload?.messageId) || _.isNil(questionId)) {
    return state;
  }

  const list = getListForMessage(state, payload.messageId);
  const existingIndex = findIndexByQuestionId(list, questionId);

  const nextEntry = {
    questionId,
    reason: payload.reason ?? null,
    tool: resolveTool(payload),
    questions: payload.questions ?? EMPTY_LIST,
    status: UserQuestionStatus.PENDING,
  };

  if (existingIndex === -1) {
    return {
      ...state,
      [payload.messageId]: [...list, nextEntry],
    };
  }

  return {
    ...state,
    [payload.messageId]: replaceAt(list, existingIndex, {
      ...list[existingIndex],
      ...nextEntry,
    }),
  };
};

export const updateQuestionStatus = (state, { messageId, questionId, changes }) => {
  if (_.isNil(messageId) || _.isNil(questionId)) {
    return state;
  }

  const list = getListForMessage(state, messageId);
  const index = findIndexByQuestionId(list, questionId);

  if (index === -1) {
    return state;
  }

  return {
    ...state,
    [messageId]: replaceAt(list, index, {
      ...list[index],
      ...changes,
    }),
  };
};

export const mergePendingQuestionsForMessage = (state, { messageId, questions }) => {
  if (_.isNil(messageId)) {
    return state;
  }

  const existingList = getListForMessage(state, messageId);
  const byId = new Map(existingList.map(question => [question.questionId, question]));

  (questions ?? []).forEach(incoming => {
    const questionId = resolveQuestionId(incoming);

    if (_.isNil(questionId)) {
      return;
    }

    const existing = byId.get(questionId);

    byId.set(questionId, {
      questionId,
      reason: incoming.reason ?? existing?.reason ?? null,
      tool: resolveTool(incoming) ?? existing?.tool ?? null,
      questions: incoming.questions ?? existing?.questions ?? EMPTY_LIST,
      status: existing?.status ?? UserQuestionStatus.PENDING,
    });
  });

  const nextList = Array.from(byId.values());
  const ownedIds = new Set(nextList.map(question => question.questionId));
  const prunedState = pruneQuestionIdsFromOtherMessages(state, messageId, ownedIds);

  if (_.isEqual(nextList, existingList) && prunedState === state) {
    return state;
  }

  return {
    ...prunedState,
    [messageId]: nextList,
  };
};
